import { useState, useEffect } from 'react';
import { Box, Tabs, Tab, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
} from '../../../common/components';
import { useModules } from '../../../hooks/useModules';

const moduleOrderLabels = {
  Bancos: 'Finanzas',
};

const moduleSubOrderLabels = {
  'Configuracion Bancaria': 'Configuracion Financiera',
  'Configuración Bancaria': 'Configuración Financiera',
  'Operaciones Bancarias': 'Operaciones Financieras',
};

const getModuleOrderLabel = (moduleOrder) => moduleOrderLabels[moduleOrder] || moduleOrder;

const getModuleSubOrderLabel = (moduleSubOrder) => (
  moduleSubOrderLabels[moduleSubOrder] || moduleSubOrder
);

const AccessDialog = ({ openAccess, handleClose, selectedProfile, handleSaveAccess }) => {
  const { data: modules = [] } = useModules();
  const [value, setValue] = useState(0);
  const [localSelectedModules, setLocalSelectedModules] = useState(new Set());

  useEffect(() => {
    if (selectedProfile?.modules) {
      const initialModules = new Set(
        selectedProfile.modules.map((profileModule) => profileModule.uuid)
      );
      setLocalSelectedModules(initialModules);
    } else {
      setLocalSelectedModules(new Set());
    }
  }, [selectedProfile, openAccess]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleCheckBoxChange = (moduleUuid, checked) => {
    setLocalSelectedModules((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(moduleUuid);
      } else {
        newSet.delete(moduleUuid);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    const selectedModulesList = Array.from(localSelectedModules);
    handleSaveAccess(selectedProfile.uuid, selectedModulesList);
  };

  const uniqueModuleOrders = [...new Set(modules.map((module) => module.module_order))];

  const groupModulesBySubOrder = (modulesList) => {
    const grouped = modulesList.reduce((acc, module) => {
      const key = module.module_sub_order || 'Otros';
      if (!acc[key]) acc[key] = [];
      acc[key].push(module);
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([subOrder, items]) => ({
        subOrder,
        items: items.sort((a, b) => a.module_name.localeCompare(b.module_name)),
      }))
      .sort((a, b) => {
        if (a.subOrder === 'Otros') return 1;
        if (b.subOrder === 'Otros') return -1;
        return a.subOrder.localeCompare(b.subOrder);
      });
  };

  const ModuleRow = ({ module }) => (
    <Box key={module.uuid} sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ width: '50%' }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localSelectedModules.has(module.uuid)}
                  onChange={(event) => handleCheckBoxChange(module.uuid, event.target.checked)}
                  name={module.uuid}
                />
              }
              label={module.module_name}
            />
          </FormGroup>
        </Box>
        <Box sx={{ width: '50%' }}>
          <div>{module.module_description}</div>
        </Box>
      </Box>
    </Box>
  );

  function TabPanel({ children, value, index, ...other }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <DialogContainer open={openAccess} onClose={handleClose} size="large">
      <DialogTitle>Accesos para perfil "{selectedProfile.name}"</DialogTitle>

      <DialogBody sx={{ display: 'block', height: '500px', width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            {uniqueModuleOrders.map((moduleOrder, index) => (
              <Tab
                key={index}
                label={getModuleOrderLabel(moduleOrder)}
                id={`simple-tab-${index}`}
                aria-controls={`simple-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>
        {uniqueModuleOrders.map((moduleOrder, index) => {
          const matchingModules = modules.filter((module) => moduleOrder === module.module_order);
          const groupedModules = groupModulesBySubOrder(matchingModules);

          return (
            <TabPanel key={index} value={value} index={index}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {groupedModules.map(({ subOrder, items }) => (
                  <Box
                    key={subOrder}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                    }}
                  >
	                    <Box
	                      component="h2"
	                      sx={{
	                        mt: 0,
	                        mb: 1.5,
	                        fontSize: '1rem',
	                        fontWeight: 600,
	                        color: 'text.primary',
	                      }}
	                    >
	                      {getModuleSubOrderLabel(subOrder)}
	                    </Box>
                    {items.map((module) => (
                      <ModuleRow key={module.uuid} module={module} />
                    ))}
                  </Box>
                ))}
              </Box>
            </TabPanel>
          );
        })}
      </DialogBody>

      <DialogActions handleClose={handleClose} handleSave={handleSave} />
    </DialogContainer>
  );
};

export default AccessDialog;
